// src/common/services/purchase-requisition.api.ts
import { IPurchaseRequisition, STATUS_ITEM } from "@/types/purchaseRequisition.type";
import { ApiResponseType, makeRequest } from "../helpers/api.helpers";
import { createDebounce } from "../helpers/debounce.helper";
import { ApprovedRow } from "@/modules/purchase-requisition/components/Modal/ModalValidatePurchaseRequisition";

const API_URL = `${import.meta.env.VITE_API_URL}/purchase-requisitions`;
const makeRequestDebounced = createDebounce(makeRequest, 200);

export type GetPurchaseRequisitionOptions = {
  status?: string[];
  search?: string;
  page?: number;
  itemsPerPage?: string;
  orderBy?: string;
  fields?: string;
};

export type DraftOrder = {
  supplierId: string;
  supplierName: string; 
  items: ApprovedRow[];
  files: File[] | string[] | FileList;
  deliveryDate?: Date | string;
}

export async function getPurchaseRequisition(
  options: GetPurchaseRequisitionOptions = {}
) {

  const {
    status = [],
    search = "",
    page = 1,
    itemsPerPage = "10",
    orderBy = "",
    fields = "_id, date, code, requestorName, companyId, status, itens, observation, priority, licensePlate, deliveryForecast, situation, budgets, createdAt, updatedAt",
  } = options;

  const params = new URLSearchParams({
    page: String(page),
    itemsPerPage: itemsPerPage,
    fields,
    orderBy
  });

  const normalize = v =>
    Array.isArray(v)
      ? (v.length ? v.join(",") : null)
      : (typeof v === "string" ? v.trim() || null : null);

  const statusParam = normalize(status);

  statusParam && params.append("filters[status]", statusParam);

  search && params.append("search", search);

  const url = `${API_URL}/?${params.toString()}`;
  return (await makeRequestDebounced(
    url
  )) as ApiResponseType<IPurchaseRequisition[]> & { totalItems: number };
}

export async function getPurchaseRequisitionById(requisitionId: string) {
  const url = `${API_URL}/${requisitionId}`;
  return (await makeRequest(url)) as ApiResponseType<IPurchaseRequisition>;
}

export async function validatePurchaseRequisition(
  requisitionId,
  approvedItens
) {
  const url = `${API_URL}/${requisitionId}/validate`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(approvedItens),
  };

  return (await makeRequest(
    url,
    options
  )) as ApiResponseType<IPurchaseRequisition>;
}

export async function changeStatusPurchaseRequisition(payload: {
  requisitionId: string,
  status: STATUS_ITEM,
  reason: string;
}) {
  const url = `${API_URL}/changeStatus/${payload.requisitionId}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };

  return (await makeRequest(
    url,
    options
  )) as ApiResponseType<IPurchaseRequisition>;
}


export async function createPurchaseRequisition(
  data: Partial<IPurchaseRequisition>
) {
  const url = `${API_URL}/`;
  const options = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return (await makeRequest(
    url,
    options
  )) as ApiResponseType<IPurchaseRequisition>;
}

export async function updatePurchaseRequisition(
  id: string,
  data: Partial<IPurchaseRequisition>
) {
  const url = `${API_URL}/${id}`;
  const options = {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  };

  return (await makeRequest(
    url,
    options
  )) as ApiResponseType<IPurchaseRequisition>;
}

